import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";
import Pagination from "../../components/common/Pagination";
import ConfirmModal from "../../components/common/ConfirmModal";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const MODE_COLORS = {
    Cash: { bg: "#dcfce7", color: "#15803d" },
    Cheque: { bg: "#e8f1f8", color: "#002747" },
    NEFT: { bg: "#fef3c7", color: "#92400e" },
    UPI: { bg: "#f3e8ff", color: "#7c3aed" },
    DD: { bg: "#f3f4f6", color: "#6b7280" },
};

const MONTHS = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const ADMISSION_TYPE_OPTIONS = ["CAP-1", "CAP-2", "CAP-3", "CAP-4", "Institute Level"];
const ADMISSION_CATEGORY_OPTIONS = [
    "Open",
    "OBC",
    "SC",
    "ST",
    "NT-A",
    "NT-B",
    "NT-C",
    "NT-D",
    "VJ",
    "SBC",
    "TFWS",
    "Open Minority Scholarship",
    "EWS",
];

export default function FeeReceiptList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { academicYear, role, selectedCollegeId, collegeData, collegeName } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";

    const [receipts, setReceipts] = useState([]);
    const [courses, setCourses] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const currentYear = new Date().getFullYear();
    const [searchTerm, setSearchTerm] = useState("");
    const [period, setPeriod] = useState("all"); // "all" | "today" | "week" | "month" | "year"
    const [filterMonth, setFilterMonth] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [filterMode, setFilterMode] = useState(() => searchParams.get("paymentMode") || "");
    const [filterCategory, setFilterCategory] = useState(() => searchParams.get("feeCategory") || searchParams.get("category") || "");
    const [filterFineType, setFilterFineType] = useState(() => searchParams.get("fineType") || "");
    const [filterCourseAdmitted, setFilterCourseAdmitted] = useState(() => searchParams.get("course") || searchParams.get("courseAdmitted") || "");
    const [filterCourseYear, setFilterCourseYear] = useState(() => searchParams.get("courseYear") || "");
    const [filterAdmissionType, setFilterAdmissionType] = useState("");
    const [filterAdmissionCategory, setFilterAdmissionCategory] = useState("");

    // ── Fetch courses from courseService ─────────────────────────────────────
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const params = { isActive: true };
                if (selectedCollegeId && selectedCollegeId !== "all") {
                    params.college = selectedCollegeId;
                }
                const res = await courseService.getAll(params);
                const courseList = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
                setCourses(courseList);
            } catch (err) {
                console.error("Failed to fetch courses:", err);
            }
        };
        fetchCourses();
    }, [selectedCollegeId]);

    // Derived year options based on selected course
    const selectedCourse = courses.find((c) => (c.shortName || c.courseName) === filterCourseAdmitted);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];

    const handleCourseChange = (e) => {
        setFilterCourseAdmitted(e.target.value);
        setFilterCourseYear(""); // Reset year when course changes
    };

    const fetchReceipts = useCallback(async () => {
        if (!academicYear?._id) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const params = {
                academicYear: academicYear._id,
                page,
                limit: 20,
            };
            if (selectedCollegeId && selectedCollegeId !== "all") {
                params.college = selectedCollegeId;
            }
            if (searchTerm.trim()) params.search = searchTerm.trim();
            if (period !== "all") params.period = period;
            if (filterMonth) params.month = filterMonth;
            if (filterYear) params.year = filterYear;
            if (filterMode) params.paymentMode = filterMode;
            if (filterCategory) params.feeCategory = filterCategory;
            if (filterFineType) params.fineType = filterFineType;
            if (filterCourseAdmitted) params.courseAdmitted = filterCourseAdmitted;
            if (filterCourseYear) params.courseYear = filterCourseYear;
            if (filterAdmissionType) params.admissionType = filterAdmissionType;
            if (filterAdmissionCategory) params.admissionCategory = filterAdmissionCategory;

            const res = await feeService.getAll(params);
            setReceipts(res.data);
            setPagination(res.pagination);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [
        academicYear,
        selectedCollegeId,
        page,
        searchTerm,
        period,
        filterMonth,
        filterYear,
        filterMode,
        filterCategory,
        filterFineType,
        filterCourseAdmitted,
        filterCourseYear,
        filterAdmissionType,
        filterAdmissionCategory,
    ]);

    useEffect(() => { fetchReceipts(); }, [fetchReceipts]);
    useEffect(() => {
        setPage(1);
    }, [
        searchTerm,
        period,
        filterMonth,
        filterYear,
        filterMode,
        filterCategory,
        filterFineType,
        filterCourseAdmitted,
        filterCourseYear,
        filterAdmissionType,
        filterAdmissionCategory,
        academicYear,
    ]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await feeService.remove(deleteTarget._id);
            toast.success(`Receipt ${deleteTarget.receiptNo} deleted`);
            setDeleteTarget(null);
            fetchReceipts();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const totalOnPage = receipts.reduce((s, r) => s + r.amountPaid, 0);

    const yearListOptions = [];
    for (let y = currentYear + 1; y >= 2020; y--) yearListOptions.push(y);

    const hasActiveFilters = Boolean(
        searchTerm ||
        period !== "all" ||
        filterMonth ||
        filterYear ||
        filterMode ||
        filterCategory ||
        filterFineType ||
        filterCourseAdmitted ||
        filterCourseYear ||
        filterAdmissionType ||
        filterAdmissionCategory
    );

    const clearFilters = () => {
        setSearchTerm("");
        setPeriod("all");
        setFilterMonth("");
        setFilterYear("");
        setFilterMode("");
        setFilterCategory("");
        setFilterFineType("");
        setFilterCourseAdmitted("");
        setFilterCourseYear("");
        setFilterAdmissionType("");
        setFilterAdmissionCategory("");
    };

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>Fee Receipts Ledger</h1>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: !selectedCollegeId || selectedCollegeId === "all" ? "#f1f5f9" : "#e0f2fe",
                            color: !selectedCollegeId || selectedCollegeId === "all" ? "#475569" : "#0369a1",
                            border: "1px solid " + (!selectedCollegeId || selectedCollegeId === "all" ? "#cbd5e1" : "#bae6fd"),
                        }}>
                            {!selectedCollegeId || selectedCollegeId === "all" ? "🌐 All Campuses" : `🏢 ${collegeData?.name || collegeName}`}
                        </span>
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {academicYear
                            ? `${pagination?.total ?? "…"} receipts recorded — ${academicYear.label}`
                            : "No academic year selected"}
                    </p>
                </div>
                <Link to={`${feeBasePath}/new`} className="btn btn-primary">+ Add Receipt</Link>
            </div>

            {!academicYear && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e" }}>
                    ⚠️ Select an academic year from the Dashboard to view receipts.
                </div>
            )}

            <div className="card" style={{ marginBottom: "1.25rem" }}>
                {/* Search Bar + Quick Period Tabs */}
                <div style={{ padding: "0.85rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                    {/* Free text search */}
                    <div style={{ flex: 1, minWidth: "260px", position: "relative" }}>
                        <input
                            type="text"
                            placeholder="🔍 Search student name, roll no, enrollment, or receipt number..."
                            className="form-control"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ fontSize: "13px", padding: "6px 12px" }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm("")}
                                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8", fontSize: "14px" }}
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Period Tabs */}
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                        {[
                            { key: "all", label: "🌟 All Time" },
                            { key: "today", label: "📅 Today (Daily)" },
                            { key: "week", label: "🗓 This Week" },
                            { key: "month", label: "📆 This Month" },
                            { key: "year", label: "🎓 This Year" },
                        ].map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setPeriod(t.key)}
                                style={{
                                    padding: "5px 11px",
                                    borderRadius: "6px",
                                    border: period === t.key ? "1px solid #003b67" : "1px solid #e2e8f0",
                                    background: period === t.key ? "#003b67" : "#f8fafc",
                                    color: period === t.key ? "#fff" : "#475569",
                                    fontWeight: period === t.key ? 700 : 500,
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Additional Detailed Filters */}
                <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
                    <select className="form-control" style={{ width: 140, fontSize: "12.5px" }}
                        value={filterMonth} onChange={(e) => { setFilterMonth(e.target.value); setPeriod("all"); }}>
                        <option value="">All Months</option>
                        {MONTHS.slice(1).map((m, i) => (
                            <option key={m} value={i + 1}>{m}</option>
                        ))}
                    </select>

                    <select className="form-control" style={{ width: 110, fontSize: "12.5px" }}
                        value={filterYear} onChange={(e) => { setFilterYear(e.target.value); setPeriod("all"); }}>
                        <option value="">All Years</option>
                        {yearListOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>

                    <select className="form-control" style={{ width: 130, fontSize: "12.5px" }}
                        value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
                        <option value="">All Modes</option>
                        {["Cash", "Cheque", "NEFT", "UPI", "DD"].map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>

                    <select className="form-control" style={{ width: 140, fontSize: "12.5px" }}
                        value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                        <option value="">All Fee Heads</option>
                        <option value="Academic">Academic / Tuition</option>
                        <option value="Transport">Bus / Transport</option>
                        <option value="Fine">Fines &amp; Penalties</option>
                        <option value="Hostel">Hostel Fees</option>
                        <option value="Other">Other / Clearance</option>
                    </select>

                    <select className="form-control" style={{ width: 140, fontSize: "12.5px" }}
                        value={filterCourseAdmitted} onChange={handleCourseChange}>
                        <option value="">All Courses</option>
                        {courses.map((c) => (
                            <option key={c._id || c.shortName} value={c.shortName}>
                                {c.shortName}
                            </option>
                        ))}
                    </select>

                    <select
                        className="form-control"
                        style={{ width: 140, fontSize: "12.5px" }}
                        value={filterCourseYear}
                        onChange={(e) => setFilterCourseYear(e.target.value)}
                        disabled={!filterCourseAdmitted}
                    >
                        <option value="">{filterCourseAdmitted ? "All Course Years" : "Select Course First"}</option>
                        {yearOptions.map((y) => (
                            <option key={y.yearNumber} value={y.label}>
                                {y.label}
                            </option>
                        ))}
                    </select>

                    <select className="form-control" style={{ width: 150, fontSize: "12.5px" }}
                        value={filterAdmissionType} onChange={(e) => setFilterAdmissionType(e.target.value)}>
                        <option value="">All Admission Types</option>
                        {ADMISSION_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>

                    <select className="form-control" style={{ width: 150, fontSize: "12.5px" }}
                        value={filterAdmissionCategory} onChange={(e) => setFilterAdmissionCategory(e.target.value)}>
                        <option value="">All Categories</option>
                        {ADMISSION_CATEGORY_OPTIONS.map((category) => (
                            <option key={category} value={category}>{category}</option>
                        ))}
                    </select>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={clearFilters}
                            style={{ fontSize: "12px", color: "#dc2626", borderColor: "#fecaca" }}
                        >
                            Reset Filters
                        </button>
                    )}

                    {receipts.length > 0 && (
                        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-muted)", fontWeight: 500 }}>
                            Page total: <strong style={{ color: "#16a34a" }}>{inr(totalOnPage)}</strong>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="loading-center"><div className="spinner" />Loading receipts…</div>
                ) : receipts.length === 0 ? (
                    <div className="empty-state">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                        </svg>
                        <p>No receipts found{hasActiveFilters ? " for the selected filters" : ""}.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Receipt No.</th>
                                    <th>Student</th>
                                    <th>Installment</th>
                                    <th>Amount</th>
                                    <th>Date</th>
                                    <th>Mode</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {receipts.map((r) => {
                                    const mc = MODE_COLORS[r.paymentMode] || MODE_COLORS.DD;
                                    return (
                                        <tr key={r._id}>
                                            <td>
                                                <span style={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 600, color: "#003b67" }}>
                                                    {r.receiptNo}
                                                </span>
                                                {r.college?.code && (
                                                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "#e0f2fe", color: "#0369a1" }}>
                                                        {r.college.code}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {r.student?._id ? (
                                                    <Link
                                                        to={`${feeBasePath}/student/${r.student._id}`}
                                                        style={{ textDecoration: "none", color: "inherit" }}
                                                    >
                                                        <div style={{ fontWeight: 500, color: "#003b67" }}>{r.student?.fullName}</div>
                                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                            {r.student?.enrollmentNo} · {r.student?.courseAdmitted} {r.student?.courseYear}
                                                        </div>
                                                    </Link>
                                                ) : (
                                                    <>
                                                        <div style={{ fontWeight: 500 }}>{r.student?.fullName}</div>
                                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                            {r.student?.enrollmentNo} · {r.student?.courseAdmitted} {r.student?.courseYear}
                                                        </div>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                <span className="badge badge-blue" style={{ fontSize: 11.5 }}>
                                                    #{r.installmentNo}
                                                </span>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
                                                    {r.installmentLabel}
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: 600, color: "#16a34a" }}>{inr(r.amountPaid)}</td>
                                            <td style={{ fontSize: 13 }}>{r.paymentDate}</td>
                                            <td>
                                                <span style={{ background: mc.bg, color: mc.color, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 500 }}>
                                                    {r.paymentMode}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                    {r.student?._id && (
                                                        <button className="btn btn-outline btn-sm"
                                                            onClick={() => navigate(`${feeBasePath}/student/${r.student._id}`)}>View</button>
                                                    )}
                                                    <button className="btn btn-success btn-sm"
                                                        onClick={() => navigate(`${feeBasePath}/receipts/${r._id}/print`)}>🖨 Print</button>
                                                    <button className="btn btn-outline btn-sm"
                                                        onClick={() => navigate(`${feeBasePath}/receipts/${r._id}/edit`)}>Edit</button>
                                                    <button className="btn btn-danger btn-sm"
                                                        onClick={() => setDeleteTarget(r)}>Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <Pagination pagination={pagination} onPageChange={setPage} />
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Receipt"
                    message={`Delete receipt ${deleteTarget.receiptNo} (${inr(deleteTarget.amountPaid)}) for ${deleteTarget.student?.fullName}? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}